import React, { useState, useEffect } from 'react';
import { Copy, DollarSign, Users, TrendingUp, Gift, CheckCircle, Clock, X } from "lucide-react";
import { getUserReferToken, createUserReferToken, getUserReferStats } from '../../utils/api';
import ScrollToTop from '../../components/layout/ScrollToTop';

const ReferAFriend = () => {
  const [referralCode] = useState("REFER2024-USER123");
  const [token, setToken] = useState('');
  const [referStats, setReferStats] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [isOptingIn, setIsOptingIn] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchReferStats = async () => {
      try {
        const response = await getUserReferStats();
        if (response.success) {
          setReferStats(response.data);
        } else {
          console.error("Failed to fetch referral stats");
        }
      } catch (error) {
        console.error("Error fetching referral stats:", error);
      }
    };

    const fetchToken = async () => {
      try {
        const response = await getUserReferToken();
        if (response.success) {
          setToken(response.data);
          setLoaded(true);
        } else {
          console.error("Failed to fetch referral token");
        }
      } catch (error) {
        console.error("Error fetching referral token:", error);
      }

      finally {
        setLoaded(true);
      }
    };

    fetchReferStats();
      fetchToken();
  }, []);

  const copyReferralLink = () => {
    const referralLink = `${window.location.origin}/join-now?referid=${token}`;
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOptIn = async () => {
    setIsOptingIn(true);
    try {
      const response = await createUserReferToken();
      if (response.success) {
        setToken(response.data);
      }
    } catch (error) {
      console.error("Error creating referral token:", error);
    }
    setIsOptingIn(false);
  };


  const getStatusIcon = (status) => {
    switch (status) {
      case "Qualified":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "Pending":
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case "Disqualified":
        return <X className="h-4 w-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium";
    const statusClasses = status === "Qualified" 
      ? "bg-green-100 text-green-800" 
      : status === "Pending" 
      ? "bg-yellow-100 text-yellow-800" 
      : "bg-red-100 text-red-800";
    
    return (
      <span className={`${baseClasses} ${statusClasses}`}>
        {getStatusIcon(status)}
        {status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <ScrollToTop />
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-4">
            Refer Friends & Earn
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Share LeadrPro with friends and earn $50 for every qualified referral. Help grow our community while earning rewards!
          </p>
        </div>
        
        {loaded && !token && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-blue-900">Join Our Referral Program</h3>
                <p className="text-sm text-blue-700">Start earning rewards by referring friends to our platform!</p>
              </div>
              <button
                onClick={handleOptIn}
                disabled={isOptingIn}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isOptingIn ? 'Setting up...' : 'Opt In Now'}
              </button>
            </div>
          </div>
        )}


        {/* Stats Cards */}
        {loaded && token != null && referStats != null && referStats.uses != null && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg border border-border shadow-sm">
            <div className="flex items-center p-6">
              <Users className="h-8 w-8 text-purple-600 mr-3" />
              <div>
                <p className="text-2xl font-bold">
                  {referStats?.uses.length || 0}
                </p>
                <p className="text-sm text-muted-foreground">Total Referrals</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-border shadow-sm">
            <div className="flex items-center p-6">
              <CheckCircle className="h-8 w-8 text-green-600 mr-3" />
              <div>
                <p className="text-2xl font-bold">
                  {referStats?.uses.filter(x => x.status == 'Approved').length || 0}
                </p>
                <p className="text-sm text-muted-foreground">Qualified</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-border shadow-sm">
            <div className="flex items-center p-6">
              <DollarSign className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <p className="text-2xl font-bold">
                  ${referStats?.uses.filter(x => x.status == 'Approved').length * 50 || 0}
                </p>
                <p className="text-sm text-muted-foreground">Total Earned</p>
              </div>
            </div>
          </div>
        
        </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Referral Link */}
          <div className={`bg-white rounded-lg border border-border shadow-sm relative ${!token ? 'opacity-50' : ''}`}>
            {!token && (
              <div className="absolute inset-0 bg-white/50 backdrop-blur-[2px] z-10 flex items-center justify-center">
                <button
                  onClick={handleOptIn}
                  disabled={isOptingIn}
                  className="px-6 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 shadow-lg"
                >
                  {isOptingIn ? 'Setting up...' : 'Activate Referral Program'}
                </button>
              </div>
            )}
            <div className="p-6 pb-0">
              <div className="flex items-center gap-2 mb-2">
                <Gift className="h-5 w-5 text-purple-600" />
                <h3 className="text-lg font-semibold">Your Referral Link</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Share this link with friends to start earning
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex gap-2 relative">
                <input 
                  value={`${window.location.origin}/join-now?referid=${token}`}
                  readOnly
                  disabled={!token}
                  className="flex-1 px-3 py-2 border border-input rounded-md bg-background text-sm disabled:cursor-not-allowed"
                />
                <button 
                  onClick={copyReferralLink} 
                  disabled={!token}
                  className="inline-flex items-center justify-center h-10 w-10 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Copy className="h-4 w-4" />
                </button>
                {copied && (
                  <div className="absolute right-12 top-1/2 -translate-y-1/2 bg-black text-white text-xs px-2 py-1 rounded">
                    Copied!
                  </div>
                )}
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-2">Your referral code:</p>
                <span className="inline-flex items-center px-4 py-2 rounded-md border border-input bg-background text-lg font-mono">
                  {token}
                </span>
              </div>
            </div>
          </div>

          {/* Qualification Rules */}
          <div className="bg-white rounded-lg border border-border shadow-sm">
            <div className="p-6 pb-0">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <h3 className="text-lg font-semibold">Qualification Rules</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                How your friends can qualify for your $50 earning
              </p>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-semibold text-purple-600">1</span>
                  </div>
                  <div>
                    <p className="font-medium">Sign up using your referral link</p>
                    <p className="text-sm text-muted-foreground">Friend must register with your unique link</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-semibold text-purple-600">2</span>
                  </div>
                  <div>
                    <p className="font-medium">Complete 1 verified demo</p>
                    <p className="text-sm text-muted-foreground">Within 60 days of registration</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-semibold text-purple-600">3</span>
                  </div>
                  <div>
                    <p className="font-medium">Maintain active account</p>
                    <p className="text-sm text-muted-foreground">No policy violations or fraudulent activity</p>
                  </div>
                </div>
              </div>
              <div className="mt-6 p-4 bg-green-50 rounded-lg">
                <p className="text-sm font-medium text-green-800">
                  💰 Earn $50 for each qualified friend!
                </p>
                <p className="text-xs text-green-600 mt-1">
                  Payments processed monthly via your preferred method
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Tracking Table */}
        {loaded && token != null && (
        <div className="bg-white rounded-lg border border-border shadow-sm">
          <div className="p-6 pb-0">
            <h3 className="text-lg font-semibold mb-2">Referral Performance</h3>
            <p className="text-sm text-muted-foreground">
              Track your friends' progress and earnings
            </p>
          </div>
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-2 font-medium text-sm">Friend Email</th>
                    <th className="text-left py-3 px-2 font-medium text-sm">Status</th>
                    <th className="text-left py-3 px-2 font-medium text-sm">Date Joined</th>
                    <th className="text-left py-3 px-2 font-medium text-sm">Completed Deals</th>
                    <th className="text-left py-3 px-2 font-medium text-sm">Your Earnings</th>
                  </tr>
                </thead>

                {referStats != null && referStats.uses != null && (
                <tbody>
                  {referStats.uses.map((referral) => (
                    <tr key={referral.id} className="border-b border-border last:border-b-0">
                      <td className="py-3 px-2 font-medium">{referral.first_name} {referral.last_name}</td>
                      <td className="py-3 px-2">{getStatusBadge(referral.status)}</td>
                      <td className="py-3 px-2">{new Date(referral.date_created).toLocaleDateString()}</td>
                      <td className="py-3 px-2">
                       
                      </td>

                      <td className="py-3 px-2 font-semibold"></td>
                    </tr>
                  ))}
                  {(!referStats?.uses || referStats.uses.length === 0) && (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-muted-foreground">
                        No referrals yet. Share your link to start earning!
                      </td>
                    </tr>
                  )}
                </tbody>
                )}
              </table>
            </div>
          </div>
        </div>
        )}

        {/* Additional Info */}
        <div className="bg-white rounded-lg border border-border shadow-sm mt-8">
          <div className="p-6 pb-0">
            <h3 className="text-lg font-semibold">Important Notes</h3>
          </div>
          <div className="p-6 space-y-3 text-sm text-muted-foreground">
            <p>• Referral earnings are paid out monthly on the 1st via your chosen payment method</p>
            <p>• Self-referrals or fraudulent accounts will result in disqualification</p>
            <p>• Friends have 60 days from signup to complete 1 verified demo</p>
            <p>• Maximum of 10 qualified referrals per month per user</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferAFriend;